'use client';

import { Calendar, ExternalLink } from 'lucide-react';
import { CWE } from '../lib/types';
import { CWEHistoricalRanking } from '../data/top25-all-years';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs';

interface CWEHeroProps {
  cwe: CWE;
  historicalRanking?: CWEHistoricalRanking;
  breadcrumbs?: BreadcrumbItem[];
}

function getHeroGradient(abstraction: string, likelihood?: string): string {
  // If high likelihood of exploit, use more urgent colors
  if (likelihood === 'High') {
    return 'from-red-700 to-red-500';
  }
  if (likelihood === 'Medium') {
    return 'from-orange-600 to-amber-500';
  }

  // Otherwise, base on abstraction level
  switch (abstraction) {
    case 'Class':
      return 'from-blue-700 to-blue-500';
    case 'Base':
      return 'from-green-700 to-green-500';
    case 'Variant':
      return 'from-yellow-600 to-yellow-500';
    default:
      return 'from-gray-600 to-gray-400';
  }
}

function getLikelihoodBadge(likelihood?: string) {
  switch (likelihood) {
    case 'High':
      return 'bg-red-600 text-white';
    case 'Medium':
      return 'bg-orange-500 text-white';
    case 'Low':
      return 'bg-green-600 text-white';
    default:
      return 'bg-gray-500 text-white';
  }
}

export default function CWEHero({ cwe, historicalRanking, breadcrumbs }: CWEHeroProps) {
  const gradient = getHeroGradient(cwe.abstractionLevel, cwe.likelihoodOfExploit);
  const latestRanking = historicalRanking?.rankings[historicalRanking.rankings.length - 1];

  return (
    <section className={`bg-gradient-to-br ${gradient} text-white py-8 md:py-12 px-4 md:px-6 rounded-xl shadow-lg mb-6`}>
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="mb-4 overflow-x-auto pb-2 -mx-2 px-2">
            <Breadcrumbs
              items={breadcrumbs}
              className="text-white/70 [&_a]:text-white/80 [&_a:hover]:text-white"
            />
          </div>
        )}

        {/* CWE ID and Name */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
          {cwe.cweId}: {cwe.name}
        </h1>

        {/* Key Badges */}
        <div className="flex flex-wrap gap-3 mb-6">
          {/* Abstraction Level */}
          <span className="px-4 py-2 bg-white/20 rounded-full text-sm font-medium">
            {cwe.abstractionLevel}
          </span>

          {/* Status */}
          <span className="px-4 py-2 bg-white/20 rounded-full text-sm font-medium">
            {cwe.status}
          </span>

          {/* Likelihood of Exploit */}
          {cwe.likelihoodOfExploit && (
            <span className={`px-4 py-2 rounded-full text-sm font-bold ${getLikelihoodBadge(cwe.likelihoodOfExploit)}`}>
              Exploit Likelihood: {cwe.likelihoodOfExploit}
            </span>
          )}

          {/* Top 25 Badge */}
          {latestRanking?.rank && (
            <span className="px-4 py-2 bg-purple-600 rounded-full text-sm font-bold">
              🏆 #{latestRanking.rank} in Top 25 ({latestRanking.year})
            </span>
          )}
        </div>

        {/* Quick description */}
        <p className="text-base md:text-lg text-white/90 leading-relaxed line-clamp-3 max-w-4xl mb-6">
          {cwe.description}
        </p>

        {/* External Link */}
        <a
          href={`https://cwe.mitre.org/data/definitions/${cwe.id}.html`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
        >
          View on MITRE
          <ExternalLink className="w-4 h-4" />
        </a>

        {/* Stats if in Top 25 */}
        {latestRanking && (
          <div className="flex flex-wrap gap-6 mt-6 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white">{latestRanking.cveCount.toLocaleString()}</span>
              <span>Related CVEs</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white">{latestRanking.score.toFixed(2)}</span>
              <span>Severity Score</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
